import { B2C_ONBOARDING_PAGES } from "core/consts";
import { B2COnboardingPage } from "core/types";
import RSButton from "ds_legacy/components/RSButton";
import { TILE_WIDTH } from "ds_legacy/components/TileButton";
import { APP_BACKGROUND } from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import {
  dp,
  margin,
  padding,
  responsiveCSS,
  sizing,
} from "ds_legacy/materials/metrics";
import {
  Caption,
  FONT_SIZE_24,
  FONT_SIZE_30,
  Heading,
} from "ds_legacy/materials/typography";
import {
  B2CFilterActions,
  B2CFilters,
} from "dsl/organisms/Filters/B2CApp/types";
import { ChevronRightIcon } from "lucide-react";
import { ComponentProps, FC, ReactNode } from "react";
import styled, { css } from "styled-components";
import { useTranslations } from "translations";
import Translations from "translations/types";

export type PageProps = {
  filters: B2CFilters;
  navigateToPage: (destination: B2COnboardingPage) => void;
  setFilters: React.Dispatch<B2CFilterActions>;
  translations: Translations;
};

export const testIds = {
  action: "cta-action",
  [B2C_ONBOARDING_PAGES.CARE_SOLUTION]: "caresolution-page",
  [B2C_ONBOARDING_PAGES.CARE_TERM]: "careterm-page",
  [B2C_ONBOARDING_PAGES.CARE_TYPE]: "caretype-page",
  [B2C_ONBOARDING_PAGES.DAILY_LIVING_SUPPORT_SERVICES]:
    "dailylivingsupportservices-page",
  [B2C_ONBOARDING_PAGES.LOADING]: "loading-page",
  [B2C_ONBOARDING_PAGES.LOCATION]: "location-page",
  [B2C_ONBOARDING_PAGES.START_DATE]: "startdate-page",
  [B2C_ONBOARDING_PAGES.TARGET]: "target-page",
  [B2C_ONBOARDING_PAGES.WELCOME]: "welcome-page",

  locationInput: "location",
};

export const PAGE_MAX_WIDTH = sizing(51);
export const MOBILE_MAX_WIDTH = sizing(43);
const STEPPER_HEIGHT = 92;
const STEPPER_HEIGHT_MOBILE = 62;

export const PageWrapper = styled(VerticalLayout)<{
  withBackground?: boolean;
}>`
  position: relative;
  width: 100%;
  max-width: ${PAGE_MAX_WIDTH};
  margin: ${margin(0, 2)};
  ${({ withBackground }: { withBackground?: boolean }) =>
    !withBackground &&
    css`
      ${responsiveCSS({
        desktop: [`min-height: calc(100% - ${dp(STEPPER_HEIGHT)})`],
        mobile: [`min-height: calc(100% - ${dp(STEPPER_HEIGHT_MOBILE)})`],
      })};
    `};
  ${responsiveCSS({
    mobile: [`max-width: ${MOBILE_MAX_WIDTH}`],
  })};
`;

export const PageHeading = styled(Heading)`
  margin: ${margin(3, 1, 5, 1)};
  font-size: ${FONT_SIZE_30};
  ${responsiveCSS({
    mobile: [`font-size: ${FONT_SIZE_24}`, `margin: ${margin(0, 1, 2, 1)};`],
  })};
`;

const SkipButtonWrapper = styled.div`
  margin: auto 0 ${sizing(3)} 0;
  ${responsiveCSS({
    tablet: [`margin: auto auto 0 auto`],
  })};
  button {
    min-width: unset !important;
    ${responsiveCSS({
      tablet: [`margin: auto`],
    })};
  }
`;

export function SkipButton({
  onClick,
  translations,
}: {
  onClick: () => void;
  translations: Translations;
}) {
  return (
    <SkipButtonWrapper>
      <RSButton
        color="grey"
        id="skip"
        loading="na"
        style={{ margin: 0, textTransform: "none" }}
        variant="text"
        onClick={onClick}
      >
        {translations.bToC.onboardingScreens.buttonSkip}
      </RSButton>
    </SkipButtonWrapper>
  );
}

export default function OnboardingCard({
  children,
  navigateToPage,
  onNext,
  title,
  withBackground,
  ...props
}: {
  children: React.ReactNode;
  navigateToPage?: (destination: B2COnboardingPage) => void;
  onNext?: AnyFunction;
  title: string;
  withBackground?: boolean;
}) {
  const translations = useTranslations();
  return (
    <PageWrapper withBackground={withBackground} {...props}>
      <PageHeading bold primary>
        {title}
      </PageHeading>
      {children}
      {onNext && (
        <div className="mx-auto w-[calc(100%-16px)] tablet:mx-auto tablet:w-[400px]">
          <RSButton
            color="primary"
            id="dont_know"
            loading="na"
            RightIcon={ChevronRightIcon}
            variant="text"
            onClick={() => onNext()}
          >
            {translations.bToC.onboardingScreens.buttonDontKnow}
          </RSButton>
        </div>
      )}
      {navigateToPage && (
        <SkipButton
          translations={translations}
          onClick={() => navigateToPage(B2C_ONBOARDING_PAGES.LOADING)}
        />
      )}
    </PageWrapper>
  );
}
export const StepperPage: FC<
  React.PropsWithChildren<{
    background: ReactNode;
    showBackground: boolean;
    stepper: ReactNode;
  }>
> = ({ background, children, showBackground, stepper }) => {
  return (
    <StepperLayout showBackground={showBackground}>
      {showBackground && background}
      <StepperPageWrapper showBackground={showBackground}>
        {stepper}
        {children}
      </StepperPageWrapper>
    </StepperLayout>
  );
};

const StepperLayout = styled(HorizontalLayout)<
  ComponentProps<typeof HorizontalLayout> & {
    showBackground: boolean;
  }
>`
  height: 100%;
  ${({ showBackground }: { showBackground: boolean }) =>
    responsiveCSS({
      tablet: [
        `flex-direction: ${showBackground ? "column-reverse" : "column"}`,
        `justify-content: flex-start`,
      ],
    })};
`;

export const StepperPageWrapper = styled(VerticalLayout)<{
  showBackground: boolean;
}>`
  width: ${({ showBackground }: { showBackground: boolean }) =>
    showBackground ? "55%" : "100%"};
  align-items: center;
  background-color: ${APP_BACKGROUND};
  ${responsiveCSS({
    tablet: [`padding: ${padding(0)}`, `width: 100%`],
  })};
  ${({ showBackground }: { showBackground: boolean }) =>
    showBackground
      ? css`
          ${responsiveCSS({
            desktop: [`min-width: calc(100vw - ${MAX_WIDTH_BACKGROUND}px)`],
            tablet: [`height: initial`],
          })};
        `
      : css`
          ${responsiveCSS({
            tablet: [`height: 100%`],
          })};
        `}
`;

const MAX_WIDTH_BACKGROUND = 600;
export const BackgroundImage = styled.div<{ image: string }>`
  height: 100%;
  position: relative;
  ${({ image }: { image: string }) =>
    image &&
    css`
      width: 45%;
      max-width: ${dp(MAX_WIDTH_BACKGROUND)};
      background-image: url(${image});
      background-position: center;
      background-size: cover;
      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(94, 206, 220, 0.2);
      }
    `};
  ${responsiveCSS({
    tablet: [`display: none;`],
  })};
`;

export const StyledCaption = styled(Caption)`
  ${responsiveCSS({
    desktop: [`margin: ${margin(0, 1)}`],
    mobile: [`margin: 0 auto`, `width: calc(100% - ${sizing(2)})`],
    tablet: [`margin: 0 auto`, `width: ${TILE_WIDTH}`],
  })};
`;
